import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { submitBugReport } from '../api/bugReports';

const FEATURE_LABELS = [
  { id: 'study_tool', label: 'Study tool', icon: 'book' },
  { id: 'quiz_improvement', label: 'Quiz improvement', icon: 'check' },
  { id: 'flashcards', label: 'Flashcards / Cards', icon: 'layers' },
  { id: 'ui_ux', label: 'UI / Navigation', icon: 'layout' },
  { id: 'social', label: 'Social / Sharing', icon: 'users' },
  { id: 'other', label: 'Other', icon: 'star' },
];

function FeatureIcon({ name, size = 14 }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'book': return <svg {...props}><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>;
    case 'check': return <svg {...props}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>;
    case 'layers': return <svg {...props}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
    case 'layout': return <svg {...props}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
    case 'users': return <svg {...props}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
    case 'star': return <svg {...props}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    default: return null;
  }
}

function FeatureRequestModal({ isOpen, onClose }) {
  const location = useLocation();
  const [selected, setSelected] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selected || !description.trim()) return;
    setSubmitting(true);
    try {
      await submitBugReport({
        title: `[Feature] ${FEATURE_LABELS.find(l => l.id === selected)?.label || selected}`,
        description: description.trim(),
        pageUrl: location.pathname,
        category: 'feature_request',
        bugType: selected,
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setSelected(null);
        setDescription('');
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Feature request failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelected(null);
    setDescription('');
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="bug-modal-overlay" onClick={handleClose}>
      <div className="bug-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
        {submitted ? (
          <div className="bug-modal-success">
            <div className="bug-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <div className="bug-success-title">Request received</div>
            <div className="bug-success-subtitle">We'll review your idea</div>
          </div>
        ) : (
          <>
            <div className="bug-modal-header">
              <h2 className="bug-modal-title">Request a Feature</h2>
              <button className="bug-modal-close" onClick={handleClose}>&times;</button>
            </div>

            <div className="bug-modal-body">
              <div className="bug-label-grid">
                {FEATURE_LABELS.map(label => (
                  <button
                    key={label.id}
                    className={`bug-label-chip ${selected === label.id ? 'selected' : ''}`}
                    onClick={() => setSelected(label.id)}
                  >
                    <FeatureIcon name={label.icon} />
                    <span>{label.label}</span>
                  </button>
                ))}
              </div>

              <textarea
                className="bug-description-input"
                placeholder="Describe the feature you'd like to see..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <div className="bug-char-count">{description.length}/1000</div>
            </div>

            <div className="bug-modal-footer">
              <button
                className="bug-submit-button"
                onClick={handleSubmit}
                disabled={!selected || !description.trim() || submitting}
              >
                {submitting ? 'Sending...' : 'Submit Request'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FeatureRequestModal;
